import { memoize } from "es-toolkit";

import type { DesignerNodeData } from "../../schemas/index.ts";
import { isNodeId } from "../../shared/index.ts";
import type { NodeDesignsState } from "../types.ts";
import { getNodeSlotIdCreate } from "./getNodeSlotId.ts";
import { NodeDataCache } from "./nodeDataCache.ts";

const EMPTY_SLOTS = Object.freeze({});
const nodeCache = new NodeDataCache();

export const getDesignerNodeDataByIdCreate = (state: NodeDesignsState) => {
  const { designsById, slotsByParentId, versionByTypeId, upstreamTemplateIdById } = state;
  const getNodeSlotId = getNodeSlotIdCreate(state);

  const getDesignerNodeDataById: GetDesignerNodeDataById = memoize((id) => {
    const design = designsById[id];
    if (!design) {
      throw new Error(`No design found with id: ${id}.`);
    }

    const slotsByName = slotsByParentId[id] ?? EMPTY_SLOTS;

    // Always build childNodes fresh (so we get updated descendants)
    const childNodes: Record<string, DesignerNodeData | DesignerNodeData[] | undefined> = Object.fromEntries(
      Object.entries(slotsByName).map(([name, slotOrSlots = []]) => {
        const idOrIds = Array.isArray(slotOrSlots)
          ? slotOrSlots.map(({ residentId, placeholderId }) => residentId ?? placeholderId).filter(isNodeId)
          : (slotOrSlots.residentId ?? slotOrSlots.placeholderId);
        const entries = Array.isArray(idOrIds)
          ? idOrIds.map(getDesignerNodeDataById)
          : idOrIds
            ? designsById[idOrIds]
              ? getDesignerNodeDataById(idOrIds)
              : undefined
            : undefined;
        return [name, entries];
      }),
    );

    // Check if we can return cached result (when children are unchanged)
    const cached = nodeCache.getCached(design, childNodes);
    if (cached) {
      return cached;
    }

    // Build new result
    const version = versionByTypeId[design.typeId];
    const isPlaceholder = getNodeSlotId(id)?.isPlaceholder;
    const upstreamTemplateId = upstreamTemplateIdById[id];

    const result = {
      id,
      ...design,
      childNodes,
      version,
      isPlaceholder,
      upstreamTemplateId,
    };

    // Cache it
    nodeCache.set(design, childNodes, result);

    return result;
  });

  return getDesignerNodeDataById;
};

export type GetDesignerNodeDataById = (id: number) => DesignerNodeData;
